/* Partner band adapted from the official Podologie landing page.
 * Original logo assets, animation speed and 3D parameters are retained.
 * Adds a visible pause control and a static, complete logo view.
 */
(() => {
  function initializeBand(root) {
    if (root.dataset.logoInitialized) return;
    const marquee = root.querySelector('[data-logo-marquee]');
    const sequence = root.querySelector('[data-logo-sequence]');
    const track = root.querySelector('.kb-logo-track');
    if (!marquee || !sequence || !track) return;
    root.dataset.logoInitialized = 'true';

    const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const controls = root.querySelector('[data-logo-controls]');
    const pause = root.querySelector('[data-logo-pause]');
    const all = root.querySelector('[data-logo-all]');
    const clone = sequence.cloneNode(true);
    clone.removeAttribute('data-logo-sequence');
    clone.setAttribute('data-logo-clone', '');
    clone.setAttribute('aria-hidden', 'true');
    clone.setAttribute('inert', '');
    clone.querySelectorAll('img').forEach(image => { image.alt = ''; });
    sequence.after(clone);
    marquee.classList.add('is-ready', 'is-paused');
    const items = Array.from(root.querySelectorAll('.kb-logo-row li'));
    const state = { inView: false, userPaused: false, static: false, hover: false, focus: false };
    let frame = 0;
    let lastUpdate = 0;

    function shouldPlay() {
      return state.inView && !state.userPaused && !state.static && !state.hover &&
        !state.focus && !document.hidden && !motion.matches;
    }

    function resetBend() {
      items.forEach(item => {
        ['bend', 'depth', 'lift', 'scale', 'opacity'].forEach(name => {
          item.style.removeProperty(`--kb-logo-${name}`);
        });
      });
    }

    function updateBend(timestamp) {
      frame = 0;
      if (!root.isConnected || !shouldPlay()) return;
      if (timestamp - lastUpdate >= 32) {
        lastUpdate = timestamp;
        const bounds = marquee.getBoundingClientRect();
        if (bounds.width > 0) {
          const center = bounds.left + bounds.width / 2;
          const strength = window.innerWidth <= 800 ? 0.62 : 1;
          items.forEach(item => {
            const itemBounds = item.getBoundingClientRect();
            const offset = Math.max(-1, Math.min(1,
              (itemBounds.left + itemBounds.width / 2 - center) / (bounds.width / 2)));
            const distance = Math.abs(offset);
            item.style.setProperty('--kb-logo-bend', `${(-offset * 10 * strength).toFixed(2)}deg`);
            item.style.setProperty('--kb-logo-depth', `${(-distance * 22 * strength).toFixed(2)}px`);
            item.style.setProperty('--kb-logo-lift', `${(distance * 4 * strength).toFixed(2)}px`);
            item.style.setProperty('--kb-logo-scale', (1.015 - distance * 0.055 * strength).toFixed(3));
            item.style.setProperty('--kb-logo-opacity', (1 - distance * 0.16 * strength).toFixed(3));
          });
        }
      }
      frame = window.requestAnimationFrame(updateBend);
    }

    function sync() {
      const play = shouldPlay();
      marquee.classList.toggle('is-paused', !play);
      root.classList.toggle('is-static', state.static);
      if (pause) {
        pause.hidden = motion.matches || state.static;
        pause.setAttribute('aria-pressed', String(state.userPaused));
        pause.textContent = state.userPaused ? 'Bewegung fortsetzen' : 'Bewegung pausieren';
      }
      if (all) {
        all.setAttribute('aria-expanded', String(state.static));
        all.textContent = state.static ? 'Zur Logo-Reihe' : 'Alle Logos anzeigen';
      }
      if (motion.matches || state.static) resetBend();
      if (!play && frame) { window.cancelAnimationFrame(frame); frame = 0; }
      if (play && !frame) frame = window.requestAnimationFrame(updateBend);
    }

    if (controls) controls.hidden = false;
    if (pause) pause.addEventListener('click', () => {
      state.userPaused = !state.userPaused;
      sync();
    });
    if (all) all.addEventListener('click', () => {
      state.static = !state.static;
      marquee.scrollLeft = 0;
      sync();
    });
    marquee.addEventListener('pointerenter', event => {
      if (event.pointerType === 'mouse') { state.hover = true; sync(); }
    });
    marquee.addEventListener('pointerleave', () => { state.hover = false; sync(); });
    marquee.addEventListener('focusin', () => { state.focus = true; sync(); });
    marquee.addEventListener('focusout', event => {
      if (!marquee.contains(event.relatedTarget)) { state.focus = false; sync(); }
    });
    marquee.addEventListener('keydown', event => {
      if (event.key === ' ' && !motion.matches && !state.static) {
        event.preventDefault();
        state.userPaused = !state.userPaused;
        sync();
      }
    });
    document.addEventListener('visibilitychange', sync);
    if (motion.addEventListener) motion.addEventListener('change', sync);
    else if (motion.addListener) motion.addListener(sync);
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(entries => {
        state.inView = entries.some(entry => entry.isIntersecting && entry.intersectionRatio >= 0.12);
        sync();
      }, { threshold: [0, 0.12] }).observe(marquee);
    } else {
      state.inView = true;
    }
    sync();
  }

  function initializeAll() {
    document.querySelectorAll('[data-kb-partner-band]').forEach(initializeBand);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initializeAll, { once: true });
  else initializeAll();
})();
